import { useEffect, useMemo, useState } from 'react';
import { useMarketStore } from '../../stores/marketStore';
import type { FeedLogEntry } from '../../stores/marketStore';
import { getRecentTrades, hasAnalytics, type TradeRow as AnalyticsTradeRow } from '../../services/hydraAnalytics';

interface FeedTapeProps {
  onClose: () => void;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function shortTicker(ticker: string): string {
  if (ticker.startsWith('PM-')) {
    const parts = ticker.split('-');
    return parts.slice(1, 4).join('-');
  }
  const parts = ticker.split('-');
  return parts[parts.length - 1];
}

function sourceLabel(source: string): string {
  return source === 'kalshi' ? 'K' : source === 'polymarket' ? 'P' : source.slice(0, 1).toUpperCase();
}

function tradeTimestamp(row: AnalyticsTradeRow): string {
  const raw = row.block_ts ?? row.ts;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? formatTime(ms) : raw.slice(11, 23);
}

function tradePriceCents(row: AnalyticsTradeRow): number | null {
  const p = Number(row.price);
  if (!Number.isFinite(p)) return null;
  return p <= 1 ? Math.round(p * 100) : Math.round(p);
}

function tradeSizeLabel(row: AnalyticsTradeRow): string {
  const count = Number(row.count);
  if (Number.isFinite(count) && count > 0) return `${Math.round(count).toLocaleString()}ct`;
  const size = Number(row.size);
  if (Number.isFinite(size) && size > 0) return `$${Math.round(size).toLocaleString()}`;
  return 'size?';
}

function isLargeTrade(row: AnalyticsTradeRow): boolean {
  const count = Number(row.count);
  const size = Number(row.size);
  return (Number.isFinite(count) && count >= 1000) || (Number.isFinite(size) && size >= 10_000);
}

function TypeBadge({ type }: { type: string }) {
  const styles =
    type === 'market' ? 'bg-green-900/40 text-green-400' :
    type === 'consensus' ? 'bg-amber-900/40 text-amber-300' :
    'bg-yellow-900/40 text-yellow-400';
  const letter =
    type === 'market' ? 'M' :
    type === 'consensus' ? 'C' :
    'B';
  return (
    <span className={`inline-flex w-4 items-center justify-center rounded text-[9px] ${styles}`}>
      {letter}
    </span>
  );
}

// Consensus values render at tenth-of-a-cent grain when the frame carried
// tenths (hydra ≥2026-07-05) — frames emit on tenth-only moves, so showing
// the integer would make consecutive rows look like duplicate no-ops.
// Falls back to the integer cents on pre-rollout frames. Soccer 3-way
// frames append the draw; the ˢ marks Pinnacle-anchored computes (a
// sharp flip alone emits a frame with zero numeric change).
function ConsensusValue({ entry }: { entry: FeedLogEntry }) {
  const fmt = (tenths: number | undefined, cents: number | undefined) =>
    tenths != null ? (tenths / 10).toFixed(1) : String(cents);
  return (
    <span className="text-foreground/90 shrink-0 tabular-nums">
      {fmt(entry.awayProbTenths, entry.awayProbCents)}
      <span className="text-muted-foreground">/</span>
      {fmt(entry.homeProbTenths, entry.homeProbCents)}
      {entry.drawProbTenths != null && (
        <>
          <span className="text-muted-foreground">/</span>
          {(entry.drawProbTenths / 10).toFixed(1)}
        </>
      )}
      {entry.sharpBookUsed === true && (
        <span className="text-amber-300" title="Pinnacle-anchored (sharpBookUsed)">
          ˢ
        </span>
      )}
    </span>
  );
}

function FeedRow({ entry }: { entry: FeedLogEntry }) {
  return (
    <div className="border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px]">
      <span className="text-muted-foreground w-[70px] shrink-0">{formatTime(entry.ts)}</span>
      <TypeBadge type={entry.type} />
      <span className="text-muted-foreground w-6 shrink-0 text-center uppercase">{entry.sport}</span>
      <span className="text-foreground/80 min-w-0 flex-1 truncate">{shortTicker(entry.ticker)}</span>
      {entry.yesBid != null && (
        <span className="text-foreground/90 shrink-0 tabular-nums">
          {entry.yesBid}<span className="text-muted-foreground">/</span>{entry.yesAsk ?? '—'}
        </span>
      )}
      {entry.type === 'market' && (
        <span className={`shrink-0 tabular-nums ${entry.volume != null && entry.volume > 0 ? 'text-cyan-400' : 'text-muted-foreground'}`}>
          {entry.volume != null ? `v${entry.volume}` : 'v?'}
        </span>
      )}
      {entry.type === 'orderbook' && entry.bookLevels != null && (
        <span className="text-muted-foreground shrink-0">{entry.bookLevels}L</span>
      )}
      {entry.type === 'consensus' && entry.awayProbCents != null && entry.homeProbCents != null && (
        <ConsensusValue entry={entry} />
      )}
    </div>
  );
}

function FeedColumn({ title, color, entries }: { title: string; color: string; entries: FeedLogEntry[] }) {
  const marketCount = entries.filter(e => e.type === 'market').length;
  const bookCount = entries.filter(e => e.type === 'orderbook').length;
  const consensusCount = entries.filter(e => e.type === 'consensus').length;
  // Unabated only emits consensus today; Kalshi/Poly only emit market+book.
  // Show the relevant subset so the header stays tight.
  const summary = consensusCount > 0 && marketCount === 0 && bookCount === 0
    ? `${consensusCount}C`
    : `${marketCount}M / ${bookCount}B`;

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="border-border flex items-center justify-between border-b px-2 py-1.5">
        <span className={`text-[11px] font-bold ${color}`}>{title}</span>
        <span className="text-muted-foreground text-[10px]">{summary}</span>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">Waiting...</div>
        ) : (
          entries.map((entry, i) => <FeedRow key={i} entry={entry} />)
        )}
      </div>
    </div>
  );
}

function TradeFeedRow({ row }: { row: AnalyticsTradeRow }) {
  const price = tradePriceCents(row);
  const large = isLargeTrade(row);
  return (
    <div
      className={`border-border/40 hover:bg-muted/40 flex items-center gap-1.5 border-b px-2 py-0.5 font-mono text-[10px] ${
        large ? 'bg-amber-500/10' : ''
      }`}
      title={row.ticker}
    >
      <span className="text-muted-foreground w-[70px] shrink-0">{tradeTimestamp(row)}</span>
      <span
        className={`inline-flex w-4 items-center justify-center rounded text-[9px] ${
          row.source === 'kalshi'
            ? 'bg-blue-900/40 text-blue-300'
            : 'bg-purple-900/40 text-purple-300'
        }`}
      >
        {sourceLabel(row.source)}
      </span>
      <span className="text-muted-foreground w-6 shrink-0 text-center uppercase">
        {row.sport ?? '—'}
      </span>
      <span className="text-foreground/80 min-w-0 flex-1 truncate">{shortTicker(row.ticker)}</span>
      <span className="text-foreground/90 shrink-0 tabular-nums">
        {price == null ? '—' : `${price}¢`}
      </span>
      <span className={large ? 'shrink-0 rounded bg-amber-400/20 px-1 text-amber-200 light:text-amber-800 tabular-nums' : 'text-cyan-400 shrink-0 tabular-nums'}>
        {tradeSizeLabel(row)}
      </span>
    </div>
  );
}

function TradeFeedColumn({
  rows,
  error,
}: {
  rows: AnalyticsTradeRow[];
  error: string | null;
}) {
  const largeCount = rows.filter(isLargeTrade).length;
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="border-border flex items-center justify-between border-b px-2 py-1.5">
        <span className="text-[11px] font-bold text-cyan-300">Trades</span>
        <span className="text-muted-foreground text-[10px]">{rows.length} / {largeCount}L</span>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {error ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">{error}</div>
        ) : rows.length === 0 ? (
          <div className="text-muted-foreground px-2 py-3 text-center text-[10px]">Waiting...</div>
        ) : (
          rows.map((row, i) => (
            <TradeFeedRow
              key={row.external_trade_id ?? row.tx_hash ?? `${row.ts}-${row.ticker}-${i}`}
              row={row}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function FeedTape({ onClose }: FeedTapeProps) {
  const getFeedLog = useMarketStore((s) => s.getFeedLog);
  const version = useMarketStore((s) => s.version);
  const [tradeRows, setTradeRows] = useState<AnalyticsTradeRow[]>([]);
  const [tradeError, setTradeError] = useState<string | null>(null);

  const kalshiEntries = useMemo(() => {
    void version;
    return getFeedLog('kalshi');
  }, [getFeedLog, version]);

  const polyEntries = useMemo(() => {
    void version;
    return getFeedLog('polymarket');
  }, [getFeedLog, version]);

  const unabatedEntries = useMemo(() => {
    void version;
    return getFeedLog('unabated');
  }, [getFeedLog, version]);

  useEffect(() => {
    if (!hasAnalytics()) {
      setTradeError('Analytics off');
      return;
    }
    let cancelled = false;
    let controller: AbortController | null = null;
    const load = async () => {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      try {
        const res = await getRecentTrades({ limit: 50 }, current.signal);
        if (cancelled) return;
        setTradeRows(res.items ?? []);
        setTradeError(null);
      } catch (err) {
        if (cancelled || current.signal.aborted) return;
        setTradeError(err instanceof Error ? err.message : 'Trade feed unavailable');
      }
    };
    void load();
    const id = window.setInterval(() => void load(), 5000);
    return () => {
      cancelled = true;
      controller?.abort();
      window.clearInterval(id);
    };
  }, []);

  return (
    <div className="bg-background/95 border-border absolute bottom-full right-0 z-50 mb-1 w-[1180px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border shadow-2xl">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-3 py-1.5">
        <span className="text-foreground text-xs font-semibold">Live Feed</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground px-1 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Live streams + recent trades */}
      <div className="divide-border flex divide-x">
        <FeedColumn title="Kalshi" color="text-blue-400" entries={kalshiEntries} />
        <FeedColumn title="Polymarket" color="text-purple-400" entries={polyEntries} />
        <FeedColumn title="Unabated" color="text-amber-300" entries={unabatedEntries} />
        <TradeFeedColumn rows={tradeRows} error={tradeError} />
      </div>
    </div>
  );
}
